import { useState } from "react";
import { X } from "lucide-react";
import type { GrilleSaisieRow } from "../../../components/gestion/GrilleSaisieOperations";
import { parseMontantCell } from "../../../components/gestion/GrilleSaisieOperations";
import { generateUUID } from "@/lib/utils";

type ImportCSVProps = {
  open: boolean;
  onClose: () => void;
  onImport: (rows: GrilleSaisieRow[]) => void;
};

function parseDateCsv(s: string): string | null {
  const t = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const dd = m[1].padStart(2, "0");
    const mm = m[2].padStart(2, "0");
    return `${m[3]}-${mm}-${dd}`;
  }
  return null;
}

export function parseCsvSaisieOperations(content: string): { rows: GrilleSaisieRow[]; errors: string[] } {
  const errors: string[] = [];
  const lines = content.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const rows: GrilleSaisieRow[] = [];
  let lineNo = 0;
  for (const line of lines) {
    lineNo += 1;
    const parts = line.split(";").map((p) => p.trim());
    if (parts.length < 2) {
      errors.push(`Ligne ${lineNo} : colonnes insuffisantes (attendu date;libelle;debit;credit).`);
      continue;
    }
    const [ds, lib, debS, credS = ""] = parts;
    const date = parseDateCsv(ds);
    if (!date) {
      errors.push(`Ligne ${lineNo} : date invalide « ${ds} » (JJ/MM/AAAA ou AAAA-MM-JJ).`);
      continue;
    }
    const debitStr = debS || "";
    const creditStr = credS || "";
    const d = parseMontantCell(debitStr);
    const c = parseMontantCell(creditStr);
    if (d != null && c != null && d > 0 && c > 0) {
      errors.push(`Ligne ${lineNo} : débit et crédit renseignés.`);
      continue;
    }
    if (d == null && c == null) {
      errors.push(`Ligne ${lineNo} : renseigner un débit ou un crédit.`);
      continue;
    }
    rows.push({
      id: crypto.randomUUID(),
      date,
      libelle: lib || "—",
      debit: d != null && d > 0 ? debitStr : "",
      credit: c != null && c > 0 ? creditStr : "",
    });
  }
  return { rows, errors };
}

export default function ImportCSV({ open, onClose, onImport }: ImportCSVProps) {
  const [raw, setRaw] = useState("");
  const [preview, setPreview] = useState<GrilleSaisieRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);

  if (!open) return null;

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      setRaw(text);
      const { rows, errors } = parseCsvSaisieOperations(text);
      setPreview(rows);
      setParseErrors(errors);
    };
    reader.readAsText(file, "UTF-8");
  };

  const handlePreviewClick = () => {
    const { rows, errors } = parseCsvSaisieOperations(raw);
    setPreview(rows);
    setParseErrors(errors);
  };

  const handleImport = () => {
    if (preview.length === 0) return;
    onImport(preview);
    setRaw("");
    setPreview([]);
    setParseErrors([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Importer un CSV</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>
        <div className="space-y-4 overflow-y-auto p-5">
          <p className="text-sm text-slate-600">
            Format attendu : <code className="rounded bg-slate-100 px-1">date;libelle;debit;credit</code> (séparateur
            point-virgule). Date en JJ/MM/AAAA ou AAAA-MM-JJ. Débit ou crédit (pas les deux).
          </p>
          <input
            type="file"
            accept=".csv,.txt,text/csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) handleFile(f);
            }}
            className="block w-full text-sm"
          />
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Coller ou éditer le contenu</label>
            <textarea
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              rows={6}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm"
              placeholder="2025-01-15;Virement fournisseur;150000;&#10;15/01/2025;Encaissement client;;200000"
            />
            <button
              type="button"
              onClick={handlePreviewClick}
              className="mt-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              Actualiser l’aperçu
            </button>
          </div>
          {parseErrors.length > 0 ? (
            <ul className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {parseErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          ) : null}
          {preview.length > 0 ? (
            <div>
              <p className="mb-2 text-sm font-medium text-slate-700">Aperçu ({preview.length} ligne(s))</p>
              <div className="max-h-48 overflow-auto rounded-lg border border-slate-200 text-sm">
                <table className="w-full text-left">
                  <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-2 py-1">Date</th>
                      <th className="px-2 py-1">Libellé</th>
                      <th className="px-2 py-1 text-right">Débit</th>
                      <th className="px-2 py-1 text-right">Crédit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0, 50).map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-mono text-xs">{r.date}</td>
                        <td className="px-2 py-1">{r.libelle}</td>
                        <td className="px-2 py-1 text-right font-mono">{r.debit || "—"}</td>
                        <td className="px-2 py-1 text-right font-mono">{r.credit || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={preview.length === 0}
            onClick={handleImport}
            className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-500 disabled:opacity-40"
          >
            Importer dans la grille
          </button>
        </div>
      </div>
    </div>
  );
}
